import { Box, IconButton, Paper } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import type { IndividualEntry } from '../../types/individualEntry';
import { AGE_SUBCATEGORY_LABELS } from '../../types/individualEntry';

interface Props {
  entries: IndividualEntry[];
  loading?: boolean;
  onEdit: (entry: IndividualEntry) => void;
  onDelete: (entry: IndividualEntry) => void;
}

const GENDER_LABELS: Record<'M' | 'F', string> = { M: 'Muž', F: 'Žena' };

export function IndividualEntriesTable({ entries, loading, onEdit, onDelete }: Props) {
  const columns: GridColDef<IndividualEntry>[] = [
    {
      field: 'startNumber',
      headerName: 'Č.',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => row.startNumber ?? '—',
    },
    {
      field: 'name',
      headerName: 'Jméno',
      flex: 1,
      minWidth: 180,
      valueGetter: (_v, row) => `${row.lastName} ${row.firstName}`,
      sortComparator: (a: string, b: string) => a.localeCompare(b, 'cs'),
      renderCell: ({ row }) => (
        <Box sx={{ fontWeight: 500 }}>
          {row.lastName} {row.firstName}
        </Box>
      ),
    },
    {
      field: 'sdhUnitName',
      headerName: 'SDH',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'gender',
      headerName: 'Pohlaví',
      width: 100,
      renderCell: ({ row }) => GENDER_LABELS[row.gender],
    },
    {
      field: 'ageSubcategory',
      headerName: 'Věková kategorie',
      width: 180,
      renderCell: ({ row }) => AGE_SUBCATEGORY_LABELS[row.ageSubcategory],
    },
    {
      field: 'actions',
      headerName: 'Akce',
      width: 110,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) => (
        <Box>
          <IconButton size="small" onClick={() => onEdit(row)} aria-label="Upravit">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(row)}
            aria-label="Smazat"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Paper variant="outlined">
      <DataGrid
        rows={entries}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
          sorting: { sortModel: [{ field: 'startNumber', sort: 'asc' }] },
        }}
        localeText={{
          noRowsLabel: 'V této kategorii zatím nejsou jednotlivci.',
        }}
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
        }}
      />
    </Paper>
  );
}
